import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TranslationProvider, useTranslation } from '../../shared/contexts/TranslationContext';
import { LanguageToggle } from '../../shared/components/LanguageToggle';
import { AdminLink } from '../../shared/components/AdminLink';

const StoryboardScene = ({ id, overline, title, description, imagePath, reverse = false, isHero = false }) => {
  return (
    <section 
      id={id} 
      className="relative w-full h-screen flex flex-col items-center justify-center p-8 md:p-16 bg-[#F6EFE9] border-b border-black/5 snap-start shrink-0"
    >
      <div className={`max-w-7xl w-full h-full flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center justify-center gap-12 md:gap-24`}>
        
        {/* Text Content */}
        <div className="flex-1 flex flex-col gap-6 items-start justify-center text-left max-w-xl z-10">
          {overline && (
            <span className="text-sm md:text-md font-bold tracking-widest text-[#1c1c1c] uppercase border-b-2 border-black inline-block pb-1">
              {overline}
            </span>
          )}
          <h2 className={`${isHero ? 'text-6xl md:text-[5rem]' : 'text-5xl md:text-6xl'} font-black leading-[1.05] tracking-tighter text-[#1c1c1c]`}>
            {title}
          </h2>
          <p className="text-lg md:text-xl text-[#1c1c1c] leading-[1.8] font-mono mt-4 font-medium">
            {description}
          </p>
        </div>

        {/* Corporate Memphis Illustration */}
        <div className="flex-1 w-full h-full flex items-center justify-center relative">
          <div className="relative w-full max-w-2xl aspect-square flex items-center justify-center">
             {/* Decorative blob behind image */}
             <div className="absolute inset-0 bg-[#E8DFD5] rounded-full blur-3xl opacity-50 transform -translate-x-10 translate-y-10"></div>
             <img 
               src={imagePath} 
               alt={title} 
               className="relative z-10 w-full h-full object-contain drop-shadow-xl transform hover:scale-[1.02] transition-transform duration-500"
             />
          </div>
        </div>

      </div>
    </section>
  );
};

function EmissorCRMDeckInner({ lang, setLang }) {
  const navigate = useNavigate();

  const { translations } = useTranslation();
  // Safe fallback
  const t = translations && translations.s0 ? translations : { s0: {}, s1: {}, s2: {}, s3: {}, s4: {}, s5: {}, s6: {}, footer: {} };


  // Scroll Navigation Logic
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Find all sections
      const sections = Array.from(document.querySelectorAll('section[id^="scene-"]'));
      if (!sections.length) return;

      // Find the currently most visible section
      let currentSectionIndex = 0;
      let minDistance = Infinity;
      
      sections.forEach((sec, index) => {
        const rect = sec.getBoundingClientRect();
        // Distance from center of screen to center of section
        const distance = Math.abs(rect.top + rect.height/2 - window.innerHeight/2);
        if (distance < minDistance) {
          minDistance = distance;
          currentSectionIndex = index;
        }
      });

      let targetIndex = currentSectionIndex;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        targetIndex = Math.min(sections.length - 1, currentSectionIndex + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        targetIndex = Math.max(0, currentSectionIndex - 1);
      }

      if (targetIndex !== currentSectionIndex) {
        sections[targetIndex].scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden snap-y snap-mandatory scroll-smooth font-sans bg-[#F6EFE9]">
      
      {/* Top Fixed Controls */}
      <div className="fixed top-6 left-6 z-50">
        <button 
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-[#1c1c1c] text-[#F6EFE9] text-sm font-bold rounded-full shadow-md hover:scale-105 transition-transform"
        >
          &lt; Back to Hub
        </button>
      </div>

      <div className="fixed top-6 right-6 z-50 flex items-center gap-4">
        <div className="relative [&>div]:static [&>div]:shadow-md">
          <LanguageToggle lang={lang} setLang={setLang} />
        </div>
        <div className="relative [&>a]:static [&>a]:shadow-md">
          <AdminLink />
        </div>
      </div>

      {/* STORYBOARD SCENES */}

      <StoryboardScene 
        id="scene-0"
        isHero={true}
        overline={t.s0.overline}
        title={t.s0.title}
        description={t.s0.description}
        imagePath="/presentation-hub/images/emissor-crm/scene6.png" // Ultimate Merger Image works great for Hero
        reverse={false}
      />

      <StoryboardScene 
        id="scene-1"
        overline={t.s1.overline}
        title={t.s1.title}
        description={t.s1.description}
        imagePath="/presentation-hub/images/emissor-crm/scene1.png"
        reverse={true}
      />

      <StoryboardScene 
        id="scene-2"
        overline={t.s2.overline}
        title={t.s2.title}
        description={t.s2.description}
        imagePath="/presentation-hub/images/emissor-crm/scene1.png"
        reverse={false}
      />

      <StoryboardScene 
        id="scene-3"
        overline={t.s3.overline}
        title={t.s3.title}
        description={t.s3.description}
        imagePath="/presentation-hub/images/emissor-crm/scene2.png"
        reverse={true}
      />

      <StoryboardScene 
        id="scene-4"
        overline={t.s4.overline}
        title={t.s4.title}
        description={t.s4.description}
        imagePath="/presentation-hub/images/emissor-crm/scene3.png"
        reverse={false}
      />

      <StoryboardScene 
        id="scene-5"
        overline={t.s5.overline}
        title={t.s5.title}
        description={t.s5.description}
        imagePath="/presentation-hub/images/emissor-crm/scene4.png"
        reverse={true}
      />

      <StoryboardScene 
        id="scene-6"
        overline={t.s6.overline}
        title={t.s6.title}
        description={t.s6.description}
        imagePath="/presentation-hub/images/emissor-crm/scene5.png"
        reverse={false}
      />

      {/* Footer */}
      <section className="relative w-full h-[30vh] flex flex-col items-center justify-center bg-[#1c1c1c] text-[#F6EFE9] snap-start shrink-0">
        <h2 className="text-4xl font-black mb-4">{t.footer.title}</h2>
        <p className="font-mono text-white/60">{t.footer.description}</p>
      </section>

    </div>
  );
}


export default function EmissorCRMDeck() {
  const [lang, setLang] = React.useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("emissor_crm_lang");
      if (stored === "pt" || stored === "en" || stored === "es") return stored;
      const nav = (navigator.language || "en").toLowerCase();
      if (nav.startsWith("es")) return "es";
      return nav.startsWith("pt") ? "pt" : "en";
    }
    return "en";
  });

  React.useEffect(() => {
    localStorage.setItem("emissor_crm_lang", lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  return (
    <TranslationProvider deck="emissor-crm" lang={lang}>
      <EmissorCRMDeckInner lang={lang} setLang={setLang} />
    </TranslationProvider>
  );
}
